/**
 * Local fallback tenant registry (dev/mock only).
 *
 * In production the tenant config arrives from the session API (per magic-link
 * token) and served branding wins over this registry. It is the local stand-in
 * so the flow renders a real brand with no backend, and documents the shape.
 * Component code NEVER references a specific tenant — it only reads `--brand*`,
 * injected by src/theme/brand.ts.
 *
 * The code default is `kychub` (on-DNA, no tenant colour baked in). The initial
 * paint can be pointed at any fallback entry via VITE_DEFAULT_TENANT.
 */

import type { TenantConfig } from '@/types';

/* Ruling (review of owner round 2026-08-28 item 4): the registry carries NO
 * cobrand defaults — the structural "Powered by KYC Hub" footer is the
 * platform credit on every screen, so a registry cobrand would double it.
 * A tenant's SERVED cobrand still renders as-is: that line is their choice. */
export const TENANTS: Record<string, TenantConfig> = {
  kychub: {
    id: 'kychub',
    name: 'KycHub CRA',
    regime: 'RBI',
    branding: { brandColor: '#A0430A', initial: 'K', logoUrl: null },
  },
  'hdfc-life': {
    id: 'hdfc-life',
    name: 'HDFC Life',
    regime: 'RBI',
    // Sample support channel — shows the shape. A tenant that lists nothing
    // gets the honest "no contact listed" line, never an invented number.
    support: {
      phone: '+91 22 6100 0000',
      email: 'vkyc.support@example.com',
      hours: 'Mon–Sat, 9am–7pm IST',
    },
    branding: {
      brandColor: '#004C8F',
      brandColorStrong: '#013C71',
      brandContrast: '#FFFFFF',
      logoUrl: null, // swap for the tenant's logo URL; initial is the fallback
      initial: 'H',
    },
  },
  'tata-aia': {
    id: 'tata-aia',
    name: 'Tata AIA',
    regime: 'RBI',
    branding: { brandColor: '#1F3D74', initial: 'T', logoUrl: null },
  },
  kotak: {
    id: 'kotak',
    name: 'Kotak',
    regime: 'RBI',
    branding: { brandColor: '#D8232A', initial: 'K', logoUrl: null },
  },
};

/** Safe code default — never a specific customer's colours. */
export const DEFAULT_TENANT_ID = 'kychub';

/** Initial brand paint; overridable via env, falls back to the safe default. */
export const INITIAL_TENANT_ID =
  import.meta.env.VITE_DEFAULT_TENANT || DEFAULT_TENANT_ID;

export function getTenant(id: string | null | undefined): TenantConfig {
  const found = id ? TENANTS[id] : undefined;
  return found ?? TENANTS[DEFAULT_TENANT_ID]!;
}
